/**
 * VideoCaptureWizard Component
 *
 * Guides users through video-based dice registration with:
 * - Instructions to roll dice in tray
 * - Real-time face coverage tracking
 * - Missing faces prompts for gap-filling
 * - Auto-advance when coverage is complete
 */

import { FaceCoverage } from '@dice-capture/shared/types/dice-registry';
import { useRef, useState, useCallback, useEffect } from 'react';
import { CameraView, CameraViewHandle, VideoCaptureResult, TorchButton, type TorchState } from '@/components/CameraView';
import { CameraTopBar } from '@/components/CameraTopBar';
import { Button } from '@/components/ui/button';
import { VideoCaptureFeedback } from '@/components/VideoCaptureFeedback';
import { cn } from '@/lib/utils';
import { useDiceRegistryStore } from '@/stores/diceRegistryStore';

const SERVICE_TAG = '[VIDEO_WIZARD]';

/** Duration for each video capture session in milliseconds */
const VIDEO_CAPTURE_DURATION_MS = 5000;

/** Interval for updating recording progress display */
const PROGRESS_UPDATE_INTERVAL_MS = 100;

/**
 * Props for VideoCaptureWizard component
 */
interface VideoCaptureWizardProps {
  /** Callback when user wants to switch to manual capture mode */
  onSwitchToManual: () => void;
  /** Callback when coverage is complete */
  onComplete: () => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Coverage progress grid showing status of each face
 */
function CoverageGrid({ faceCoverage }: { faceCoverage: FaceCoverage[] }) {
  const totalFaces = faceCoverage.length;
  const columns = totalFaces <= 6 ? 3 : totalFaces <= 12 ? 4 : 5;

  return (
    <div
      className={cn('grid gap-2', `grid-cols-${columns}`)}
      style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
      role="group"
      aria-label="Face coverage grid"
    >
      {faceCoverage.map((fc) => {
        const progress = Math.min((fc.imageCount / fc.imagesNeeded) * 100, 100);
        return (
          <div
            key={fc.faceNumber}
            className={cn(
              'relative aspect-square rounded-md flex flex-col items-center justify-center',
              'border transition-all',
              fc.isComplete
                ? 'bg-green-500/20 border-green-500/50'
                : fc.imageCount > 0
                  ? 'bg-amber-500/20 border-amber-500/50'
                  : 'bg-secondary/50 border-border'
            )}
            aria-label={`Face ${fc.faceNumber}: ${fc.imageCount} of ${fc.imagesNeeded} images`}
          >
            <span className="text-lg font-bold">{fc.faceNumber}</span>
            <span className="text-xs text-muted-foreground">
              {fc.imageCount}/{fc.imagesNeeded}
            </span>
            {/* Progress bar overlay at bottom */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-muted rounded-b-md overflow-hidden">
              <div
                className={cn(
                  'h-full transition-all duration-300',
                  fc.isComplete ? 'bg-green-500' : 'bg-amber-500'
                )}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Missing faces list with gap-fill prompts
 */
function MissingFacesList({ missingFaces }: { missingFaces: FaceCoverage[] }) {
  if (missingFaces.length === 0) {
    return (
      <div className="p-3 rounded-md bg-green-500/10 text-green-500 text-sm text-center">
        All faces have sufficient coverage!
      </div>
    );
  }

  // Show top 3 most needed faces
  const topMissing = missingFaces.slice(0, 3);

  return (
    <div className="space-y-2">
      <p className="text-sm text-muted-foreground">
        Roll to land on these faces:
      </p>
      <div className="flex gap-2 justify-center">
        {topMissing.map((fc) => (
          <div
            key={fc.faceNumber}
            className={cn(
              'px-4 py-2 rounded-md text-center',
              'bg-amber-500/20 border border-amber-500/50'
            )}
          >
            <span className="text-xl font-bold text-amber-400">{fc.faceNumber}</span>
            <span className="block text-xs text-muted-foreground">
              needs {fc.imagesNeeded - fc.imageCount} more
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Overall progress bar
 */
function OverallProgress({
  captured,
  needed
}: {
  captured: number;
  needed: number;
}) {
  const progress = Math.min((captured / needed) * 100, 100);
  const isComplete = captured >= needed;

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>Total Images</span>
        <span>
          {captured} / {needed}
        </span>
      </div>
      <div
        className="h-2 bg-secondary rounded-full overflow-hidden"
        role="progressbar"
        aria-valuenow={captured}
        aria-valuemin={0}
        aria-valuemax={needed}
      >
        <div
          className={cn(
            'h-full rounded-full transition-all duration-300',
            isComplete ? 'bg-green-500' : 'bg-primary'
          )}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}

/**
 * VideoCaptureWizard guides users through video-based dice registration
 */
export function VideoCaptureWizard({
  onSwitchToManual,
  onComplete,
  className
}: VideoCaptureWizardProps) {
  const cameraRef = useRef<CameraViewHandle>(null);
  const progressIntervalRef = useRef<number | null>(null);

  const {
    currentRegistration,
    recordRoll,
    getMissingFaces
  } = useDiceRegistryStore();

  const {
    diceType,
    faceCoverage,
    totalImagesNeeded,
    totalImagesCaptured,
    rolls
  } = currentRegistration;

  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingProgress, setRecordingProgress] = useState(0);
  const [frameCount, setFrameCount] = useState(0);
  const [cameraReady, setCameraReady] = useState(false);
  const [torchState, setTorchState] = useState<TorchState>({ supported: false, enabled: false });

  // Get missing faces for gap-fill prompts
  const missingFaces = getMissingFaces();

  // Check if coverage is complete
  const isCoverageComplete =
    totalImagesCaptured >= totalImagesNeeded &&
    missingFaces.length === 0;

  /**
   * Handle camera ready
   */
  const handleCameraReady = useCallback(() => {
    console.info(`${SERVICE_TAG} Camera ready for video capture`);
    setCameraReady(true);
  }, []);

  /**
   * Start video capture
   */
  const handleStartCapture = useCallback(async () => {
    if (!cameraRef.current || isRecording) {
      return;
    }

    console.info(`${SERVICE_TAG} Starting video capture`, {
      rollNumber: rolls.length + 1,
      diceType
    });

    setIsRecording(true);
    setRecordingProgress(0);
    setFrameCount(0);

    // Start progress update interval
    const startTime = Date.now();
    progressIntervalRef.current = window.setInterval(() => {
      const elapsed = Date.now() - startTime;
      setRecordingProgress(elapsed);
    }, PROGRESS_UPDATE_INTERVAL_MS);

    try {
      const result: VideoCaptureResult = await cameraRef.current.startVideoCapture({
        durationMs: VIDEO_CAPTURE_DURATION_MS,
        frameIntervalMs: 500,
        minSettledDurationMs: 500
      });

      // Clear progress interval
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }

      console.info(`${SERVICE_TAG} Video capture completed`, {
        totalFrames: result.allFrames.length,
        settledFrames: result.settledFrames.length,
        uniqueFrames: result.uniqueFrames.length,
        durationMs: result.durationMs
      });

      // Update frame count for display
      setFrameCount(result.settledFrames.length);

      // Record the roll (for now, detected face is null - will be updated when detection is implemented)
      // In a real implementation, the face detection would happen on the settled frames
      const rollCapture = {
        rollNumber: rolls.length + 1,
        timestamp: new Date().toISOString(),
        detectedFace: null, // TODO: Integrate face detection
        confidence: 0,
        imageCount: result.settledFrames.length,
        s3Keys: [] // TODO: Upload frames and get S3 keys
      };

      recordRoll(rollCapture);

      setIsRecording(false);
    } catch (error) {
      console.error(`${SERVICE_TAG} Video capture failed`, {
        error: error instanceof Error ? error.message : 'Unknown error'
      });

      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }

      setIsRecording(false);
    }
  }, [isRecording, rolls.length, diceType, recordRoll]);

  /**
   * Stop video capture
   */
  const handleStopCapture = useCallback(async () => {
    if (!cameraRef.current || !isRecording) {
      return;
    }

    console.info(`${SERVICE_TAG} Stopping video capture early`);

    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }

    await cameraRef.current.stopVideoCapture();
    setIsRecording(false);
  }, [isRecording]);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  // Auto-advance when coverage is complete
  useEffect(() => {
    if (isCoverageComplete) {
      console.info(`${SERVICE_TAG} Coverage complete, ready to advance`);
    }
  }, [isCoverageComplete]);

  return (
    <div className={cn('space-y-4', className)}>
      {/* Instructions */}
      <div className="text-center">
        <h2 className="text-xl font-bold mb-1">Video Capture Mode</h2>
        <p className="text-muted-foreground text-sm">
          Roll your {diceType} in the tray. We&apos;ll capture frames automatically.
        </p>
      </div>

      {/* Overall progress */}
      <OverallProgress captured={totalImagesCaptured} needed={totalImagesNeeded} />

      {/* Camera view with feedback overlay */}
      <div className="relative aspect-square max-h-[50vh] rounded-lg overflow-hidden">
        <CameraView ref={cameraRef} className="h-full" onReady={handleCameraReady} onTorchStateChange={setTorchState} />

        <CameraTopBar
          left={
            torchState.supported ? (
              <TorchButton
                enabled={torchState.enabled}
                onToggle={() => cameraRef.current?.toggleTorch()}
              />
            ) : null
          }
        />

        {/* Video capture feedback overlay */}
        {isRecording && (
          <div className="absolute top-2 left-2 right-2 z-10">
            <VideoCaptureFeedback
              isRecording={isRecording}
              currentDurationMs={recordingProgress}
              targetDurationMs={VIDEO_CAPTURE_DURATION_MS}
              frameCount={frameCount}
            />
          </div>
        )}
      </div>

      {/* Capture button */}
      <Button
        className="w-full h-14"
        variant={isRecording ? 'destructive' : 'accent'}
        onClick={isRecording ? handleStopCapture : handleStartCapture}
        disabled={!cameraReady}
      >
        {isRecording ? 'Stop Recording' : `Record Roll ${rolls.length + 1}`}
      </Button>

      {/* Face coverage grid */}
      <CoverageGrid faceCoverage={faceCoverage} />

      {/* Missing faces prompts */}
      <MissingFacesList missingFaces={missingFaces} />

      {/* Action buttons */}
      <div className="flex gap-2">
        <Button
          variant="secondary-action"
          className="flex-1"
          onClick={onSwitchToManual}
          disabled={isRecording}
        >
          Switch to Manual
        </Button>
        {isCoverageComplete && (
          <Button
            variant="accent"
            className="flex-1"
            onClick={onComplete}
          >
            Continue
          </Button>
        )}
      </div>

      {/* Roll count info */}
      <p className="text-center text-xs text-muted-foreground">
        {rolls.length} rolls captured
      </p>
    </div>
  );
}

export default VideoCaptureWizard;
